/**
 * Coal quality from the lab (M7.9): the attributes the site reports (from `/api/site`), and how a
 * value and a coverage are written. No three.js or charts here: the Overview uses it.
 */
import type { LabAttributeOut, SiteOut } from "../api/generated/model";

export type LabKey = "ash_pct" | "tm_pct" | "im_pct" | "vm_pct" | "g_index";
export type LabValues = Partial<Record<LabKey, number | null>>;

export const FALLBACK_ATTRS: LabAttributeOut[] = [
  { key: "ash_pct", label: "Ash", unit: "% dry", decimals: 2 },
  { key: "tm_pct", label: "Total moisture", unit: "%", decimals: 2 },
  { key: "im_pct", label: "Inherent moisture", unit: "%", decimals: 2 },
  { key: "vm_pct", label: "Volatile matter", unit: "%", decimals: 2 },
  { key: "g_index", label: "G index", unit: "", decimals: 0 },
];

export function labAttrs(site: SiteOut | undefined): LabAttributeOut[] {
  return site?.lab_attributes?.length ? site.lab_attributes : FALLBACK_ATTRS;
}

/** A value with its attribute's decimals, or `digits` ("—" when there is none). */
export function fmtLab(
  v: number | null | undefined,
  attr: LabAttributeOut,
  withUnit = false,
  digits: number = attr.decimals,
): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const s = v.toFixed(Math.min(attr.decimals, digits));
  return withUnit && attr.unit ? `${s} ${attr.unit}` : s;
}

/** "Ash 10.9 % dry · TM 8.1 % · …" for the attributes that have a value. */
export function labLine(values: LabValues | null | undefined, attrs: LabAttributeOut[]): string {
  if (!values) return "";
  return attrs
    .filter((a) => values[a.key as LabKey] !== null && values[a.key as LabKey] !== undefined)
    .map((a) => `${SHORT[a.key] ?? a.label} ${fmtLab(values[a.key as LabKey], a, true, 1)}`)
    .join(" · ");
}

export const SHORT: Record<string, string> = {
  ash_pct: "Ash",
  tm_pct: "TM",
  im_pct: "IM",
  vm_pct: "VM",
  g_index: "G",
};

export function coverageText(frac: number): string {
  if (frac <= 0) return "no lab results yet";
  return `lab results cover ${Math.round(frac * 100)} %`;
}
